"use client";

import { forwardRef, useRef, useState, useCallback, useEffect } from "react";
import type { CSSProperties } from "react";
import { SectionBlock } from "@/components/blocks/SectionBlock";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";
import type { Value } from "@/entities/value";

const RING_R = 126; // SVG user-units
const SVG_SIZE = 320; // logical square size
const CX = SVG_SIZE / 2;
const CY = SVG_SIZE / 2;
const CLICK_THRESHOLD = 6; // px — below this = click, above = drag

/* ─────────────────────────────────────────────────────────────
   Чистые функции (вне компонента)
───────────────────────────────────────────────────────────── */
function getActiveIdx(angle: number, n: number): number {
  const stepDeg = 360 / n;
  const norm = (((-angle) % 360) + 360) % 360;
  return Math.round(norm / stepDeg) % n;
}

function computeSlideDir(from: number, to: number, n: number): 1 | -1 {
  return ((to - from + n) % n) <= n / 2 ? 1 : -1;
}

function spiralD(
  cx: number,
  cy: number,
  maxR: number,
  turns: number,
): string {
  const pts: string[] = [];
  const steps = 320;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const theta = t * turns * 2 * Math.PI;
    const r = maxR * t;
    const x = cx + r * Math.cos(theta - Math.PI / 2);
    const y = cy + r * Math.sin(theta - Math.PI / 2);
    pts.push(`${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return pts.join(" ");
}

function headingAnim(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation:
      "slide-from-top 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards",
  };
}

/* ─────────────────────────────────────────────────────────────
   Компонент
───────────────────────────────────────────────────────────── */
interface ValuesSectionProps {
  values: Value[];
}

export const ValuesSection = forwardRef<HTMLElement, ValuesSectionProps>(({ values }, ref) => {
  const { ref: revealRef, state } = useScrollReveal("values", 0.2);

  const N = values.length;
  const STEP_DEG = 360 / N;

  /* ── визуальное состояние ── */
  const [activeIdx, setActiveIdx]   = useState(0);
  const [ringAngle, setRingAngle]   = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [slideDir, setSlideDir]     = useState<1 | -1>(1);
  // contentKey меняется только на snap/goTo — не во время live-drag
  const [contentKey, setContentKey] = useState(0);
  // scale из ResizeObserver — кольцо адаптируется под контейнер
  const [scale, setScale] = useState(1);

  /* ── mutable refs (не вызывают ре-рендер) ── */
  const ringRef      = useRef<HTMLDivElement>(null);
  const liveAngle    = useRef(0);
  const activeIdxRef = useRef(0); // зеркало activeIdx без closure-staleness
  const isDrRef      = useRef(false); // зеркало isDragging
  const scaleRef     = useRef(1);    // зеркало scale

  // указатель кольца
  const pStart = useRef({ x: 0, y: 0, ringAngle: 0, pointerAngle: 0 });
  const movedRef    = useRef(false);
  const pointerDown = useRef(false); // кнопка нажата?

  // указатель пипок (dots)
  const dotsStart    = useRef({ x: 0, idx: 0 });
  const dotsActive   = useRef(false);
  const dotsLastIdx  = useRef(-1);

  // авто-ротация
  const AUTOPLAY_MS  = 4000;
  const autoTimer    = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPaused     = useRef(false);
  // kickTimerRef разрывает циклическую зависимость goTo ↔ kickTimer
  const kickTimerRef = useRef<() => void>(() => {});

  /* ── ResizeObserver: отслеживаем реальный px-размер кольца ──
   * TODO(M11): consider container queries — currently scale() math relies on
   * px size (SVG user-units → real px ratio). CSS @container + clamp() may
   * not give equivalent fidelity for the rotational interaction math.
   * Kept as ResizeObserver intentionally (spec §6.4 deviation, scoped). */
  useEffect(() => {
    const el = ringRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const s = entry.contentRect.width / SVG_SIZE;
      scaleRef.current = s;
      setScale(s);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ── секция ref ── */
  const setRefs = (el: HTMLElement | null) => {
    revealRef(el);
    if (typeof ref === "function") ref(el);
    else if (ref)
      (ref as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  /* ── goTo: переход к индексу кратчайшим путём ── */
  const goTo = useCallback(
    (toIdx: number, dir?: 1 | -1) => {
      const resolvedDir = dir ?? computeSlideDir(activeIdxRef.current, toIdx, N);
      const targetBase = -(toIdx * STEP_DEG);
      const n = Math.round((liveAngle.current - targetBase) / 360);
      const final = targetBase + n * 360;
      liveAngle.current = final;
      activeIdxRef.current = toIdx;
      setRingAngle(final);
      setSlideDir(resolvedDir);
      setActiveIdx(toIdx);
      setContentKey((k) => k + 1);
      kickTimerRef.current(); // сброс таймера после ручного переключения
    },
    [],
  );

  /* ── привязка к ближайшему после drag ── */
  const snapToNearest = useCallback(() => {
    const snapped = Math.round(liveAngle.current / STEP_DEG) * STEP_DEG;
    const newIdx  = getActiveIdx(snapped, N);
    const dir     = computeSlideDir(activeIdxRef.current, newIdx, N);
    liveAngle.current = snapped;
    activeIdxRef.current = newIdx;
    isDrRef.current = false;
    setRingAngle(snapped);
    setSlideDir(dir);
    setActiveIdx(newIdx);
    setIsDragging(false);
    setContentKey((k) => k + 1);
    kickTimerRef.current(); // сброс таймера после drag-snap
  }, []);

  /* ── авто-ротация ── */
  const kickTimer = useCallback(() => {
    if (autoTimer.current) clearInterval(autoTimer.current);
    autoTimer.current = setInterval(() => {
      if (isPaused.current) return;
      goTo((activeIdxRef.current + 1) % N, 1);
    }, AUTOPLAY_MS);
  }, [goTo, AUTOPLAY_MS]);

  // Синхронизируем ref всегда до рендера (без deps-цикла)
  kickTimerRef.current = kickTimer;

  // Запускаем только когда секция стала видимой
  useEffect(() => {
    if (state === "idle") return;
    kickTimer();
    return () => {
      if (autoTimer.current) clearInterval(autoTimer.current);
    };
  }, [state, kickTimer]);

  /* ─── Кольцо: pointer events (STEERING-WHEEL) ──────────── */
  const onRingPointerDown = useCallback((e: React.PointerEvent) => {
    pointerDown.current = true;
    movedRef.current = false;
    // Угол указателя относительно центра кольца в момент захвата
    const el = ringRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width  / 2;
    const cy = rect.top  + rect.height / 2;
    const pa = Math.atan2(e.clientY - cy, e.clientX - cx);
    pStart.current = {
      x: e.clientX,
      y: e.clientY,
      ringAngle:    liveAngle.current,
      pointerAngle: pa,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onRingPointerMove = useCallback((e: React.PointerEvent) => {
    if (!pointerDown.current) return;

    // Порог клика по Эвклидову расстоянию
    const dex = e.clientX - pStart.current.x;
    const dey = e.clientY - pStart.current.y;
    if (!movedRef.current && Math.hypot(dex, dey) < CLICK_THRESHOLD) return;

    if (!movedRef.current) {
      movedRef.current = true;
      isDrRef.current = true;
      setIsDragging(true);
    }

    // Вычисляем текущий угол указателя относительно центра
    const el = ringRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width  / 2;
    const cy = rect.top  + rect.height / 2;
    const currentPA = Math.atan2(e.clientY - cy, e.clientX - cx);

    // Дельта угол в радианах (нормализация до [-PI, PI])
    let deltaRad = currentPA - pStart.current.pointerAngle;
    if (deltaRad >  Math.PI) deltaRad -= 2 * Math.PI;
    if (deltaRad < -Math.PI) deltaRad += 2 * Math.PI;

    const newAngle = pStart.current.ringAngle + deltaRad * (180 / Math.PI);
    liveAngle.current = newAngle;
    setRingAngle(newAngle);

    const liveIdx = getActiveIdx(newAngle, N);
    if (liveIdx !== activeIdxRef.current) {
      activeIdxRef.current = liveIdx;
      setActiveIdx(liveIdx);
    }
  }, []);

  const onRingPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!movedRef.current) {
        // Клик — находим ближайший узел к точке касания
        const el = ringRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const s = scaleRef.current;
          const sx = (e.clientX - rect.left) / s;
          const sy = (e.clientY - rect.top)  / s;
          let minDist = Infinity;
          let closestIdx = -1;
          for (let i = 0; i < N; i++) {
            const angleDeg = i * STEP_DEG + liveAngle.current;
            const angleRad = ((angleDeg - 90) * Math.PI) / 180;
            const nx = CX + RING_R * Math.cos(angleRad);
            const ny = CY + RING_R * Math.sin(angleRad);
            const dist = Math.hypot(sx - nx, sy - ny);
            if (dist < minDist) {
              minDist = dist;
              closestIdx = i;
            }
          }
          if (closestIdx !== -1 && minDist < 50) {
            goTo(closestIdx);
          }
        }
      } else {
        snapToNearest();
      }
      movedRef.current = false;
      pointerDown.current = false;
    },
    [goTo, snapToNearest],
  );

  /* ─── Dots: pointer drag ─────────────────────────────────── */
  const onDotsPointerDown = useCallback((e: React.PointerEvent) => {
    dotsStart.current = { x: e.clientX, idx: activeIdxRef.current };
    dotsLastIdx.current = activeIdxRef.current;
    dotsActive.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onDotsPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dotsActive.current) return;
      const dx   = e.clientX - dotsStart.current.x;
      const steps = Math.round(dx / 28); // 28px на шаг
      const target = ((dotsStart.current.idx + steps) % N + N) % N;
      if (target !== dotsLastIdx.current) {
        dotsLastIdx.current = target;
        goTo(target, steps > 0 ? 1 : -1);
      }
    },
    [goTo],
  );

  const onDotsPointerUp = useCallback(() => {
    dotsActive.current = false;
  }, []);

  /* ─── Рендер ─────────────────────────────────────────────── */
  const active = values[activeIdx];

  return (
    <section ref={setRefs} className="flex flex-col">
      <SectionBlock className="flex flex-col gap-fluid-2xl p-fluid-xl">

        {/* ── Заголовок ── */}
        <div className="overflow-hidden">
          <div style={headingAnim(state)} className="text-left">
            <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
              Ценности и{" "}
              <span className="text-help">преимущества</span>
            </h2>
          </div>
        </div>

        {/* ── Интерактивная зона — навес/уход паузит автоплей ── */}
        <div
          className="pt-fluid-lg border-t border-[var(--help)]/20 flex flex-col lg:flex-row items-center gap-fluid-2xl"
          onPointerEnter={() => { isPaused.current = true;  }}
          onPointerLeave={() => { isPaused.current = false; }}
        >

          {/* ── Кольцо ── */}
          <div
            ref={ringRef}
            className="relative shrink-0 w-full self-center select-none"
            style={{
              maxWidth: SVG_SIZE,
              aspectRatio: "1",
              cursor: isDragging ? "grabbing" : "grab",
              touchAction: "none",
            }}
            onPointerDown={onRingPointerDown}
            onPointerMove={onRingPointerMove}
            onPointerUp={onRingPointerUp}
            onPointerCancel={onRingPointerUp}
          >
            {/* SVG-декорации — автомасштаб через viewBox */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
              aria-hidden
            >
              {/* Архимедова спираль */}
              <path
                d={spiralD(CX, CY, RING_R + 16, 2.5)}
                fill="none"
                stroke="var(--help)"
                strokeWidth="1"
                strokeLinecap="round"
                opacity="0.18"
              />
              {/* Трек кольца */}
              <circle
                cx={CX} cy={CY} r={RING_R}
                fill="none"
                stroke="var(--help)"
                strokeWidth="1"
                strokeDasharray="4 7"
                opacity="0.28"
              />
              {/* Центральный глоу */}
              <circle cx={CX} cy={CY} r={58} fill="var(--help)" opacity="0.07" />
              <circle cx={CX} cy={CY} r={38} fill="var(--help)" opacity="0.07" />
              {/* Индикатор активной позиции */}
              <polygon
                points={`${CX},${CY - RING_R - 11} ${CX - 5},${CY - RING_R - 4} ${CX + 5},${CY - RING_R - 4}`}
                fill="var(--help)"
                opacity="0.75"
              />
            </svg>

            {/* Центр — номер активного пункта */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                key={`n-${activeIdx}`}
                className="font-heading font-bold text-help leading-none select-none"
                style={{
                  fontSize: Math.max(28, Math.round(50 * scale)),
                  opacity: 0,
                  animation:
                    "star-bounce-zoom 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
                }}
              >
                {active.id}
              </div>
            </div>

            {/* Узлы на кольце */}
            {values.map((v, i) => {
              const angleDeg = i * STEP_DEG + ringAngle;
              const angleRad = ((angleDeg - 90) * Math.PI) / 180;
              const x = (CX + RING_R * Math.cos(angleRad)) * scale;
              const y = (CY + RING_R * Math.sin(angleRad)) * scale;
              const isActive = i === activeIdx;
              
              // Раздули активную (100) и неактивные (64)
              const bSize  = Math.max(48, Math.round((isActive ? 100 : 64) * scale));
              // Уронили кегль, чтобы цифры не прижимались к краям (14 и 11)
              const fSize  = Math.max(10, Math.round((isActive ? 14 : 11) * scale));

              return (
                <div
                  key={v.id}
                  className="absolute pointer-events-none"
                  style={{
                    left: x,
                    top: y,
                    transform: "translate(-50%, -50%)",
                    transition: isDragging
                      ? "none"
                      : "left 0.45s cubic-bezier(0.34,1.56,0.64,1), top 0.45s cubic-bezier(0.34,1.56,0.64,1)",
                    zIndex: isActive ? 10 : 1,
                  }}
                >
                  <div
                    className="relative flex items-center justify-center"
                    style={{
                      width:  bSize,
                      height: bSize,
                      transition: "width 0.3s, height 0.3s",
                    }}
                  >
                    <svg
                      className="absolute inset-0 w-full h-full"
                      viewBox="0 0 30 28"
                      style={{
                        fill: isActive ? "var(--help)" : "rgba(6, 155, 147, 0.12)",
                        filter: isActive ? "drop-shadow(0 4px 12px rgba(6, 155, 147, 0.12))" : "none",
                        transition: "fill 0.3s, filter 0.3s"
                      }}
                    >
                      <path d="M14.671 27.2119C14.671 17.6439 18.923 13.7109 29.129 13.817V13.4979C18.923 13.4979 14.671 9.45801 14.671 0H14.352C14.352 9.45801 10.206 13.4979 0 13.4979V13.817C10.206 13.7109 14.352 17.6439 14.352 27.2119H14.671Z" />
                    </svg>

                    <span
                      className="relative z-10 font-heading font-bold leading-none select-none"
                      style={{
                        fontSize: fSize,
                        color: isActive ? "white" : "var(--help)",
                        transition: "font-size 0.3s, color 0.3s",
                      }}
                    >
                      {v.id}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Панель контента ── */}
          <div className="flex-1 min-w-0 flex flex-col gap-fluid-lg">

            {/*
              CSS-grid stacking: все слайды в одной ячейке (gridRow/Col = 1).
              Высота контейнера = высота самого длинного слайда — никаких прыжков.
              Неактивные слайды: visibility:hidden (не видны, но участвуют в layout).
              Активный слайд: visibility:visible + анимация через key-remount.
            */}
            <div
              className="overflow-hidden"
              style={{ display: "grid", gridTemplateColumns: "1fr" }}
            >
              {values.map((v, i) => {
                const isActive = i === activeIdx;
                return (
                  <div
                    key={i}
                    style={{
                      gridColumn: 1,
                      gridRow: 1,
                      visibility: isActive ? "visible" : "hidden",
                    }}
                  >
                    <div
                      key={isActive ? contentKey : i}
                      style={
                        isActive
                          ? {
                              opacity: 0,
                              animation:
                                slideDir === 1
                                  ? "slide-from-right-far 0.42s cubic-bezier(0.25,0.46,0.45,0.94) forwards"
                                  : "slide-from-left-far  0.42s cubic-bezier(0.25,0.46,0.45,0.94) forwards",
                            }
                          : undefined
                      }
                    >
                      <p className="font-content font-semibold text-help text-fluid-sm mb-fluid-xs tracking-widest uppercase">
                        {v.id}
                      </p>
                      <h3 className="font-heading font-semibold text-fluid-2xl leading-tight text-foreground mb-fluid-sm">
                        {v.title}
                      </h3>
                      <p className="font-content text-fluid-base leading-relaxed text-foreground/75">
                        {v.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Пагинация-пилюли + возможность drag ── */}
            <div
              className="flex items-center gap-2 flex-wrap select-none"
              style={{ cursor: "grab", touchAction: "pan-y" }}
              onPointerDown={onDotsPointerDown}
              onPointerMove={onDotsPointerMove}
              onPointerUp={onDotsPointerUp}
              onPointerCancel={onDotsPointerUp}
            >
              {values.map((_, i) => (
                <div
                  key={i}
                  className="shrink-0 rounded-full transition-all duration-300"
                  style={{
                    width:  i === activeIdx ? 32 : 10,
                    height: 10,
                    background:
                      i === activeIdx
                        ? "var(--help)"
                        : "rgba(6,155,147,0.22)",
                  }}
                />
              ))}
            </div>

          </div>
        </div>

      </SectionBlock>
    </section>
  );
});

ValuesSection.displayName = "ValuesSection";
